import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import KpiTile from "@/components/dashboard/KpiTile";
import { formatLKR, formatPct } from "@/lib/api";
import { Layers, Wallet, TrendingUp, Coins, ArrowUpRight, ArrowDownRight, ChevronRight } from "lucide-react";

const PortfolioCombinedView = ({ aggregate, onSelectPortfolio }) => {
  const t = aggregate.totals;
  const rows = aggregate.breakdown || [];
  const totalPositive = (t.gain_loss_lkr || 0) >= 0;

  return (
    <div className="space-y-5" data-testid="portfolio-combined-view">
      <Card className="border bg-card">
        <CardContent className="p-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center">
            <Layers className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold">All Portfolios — combined</div>
            <div className="text-xs text-muted-foreground">
              {t.portfolios_count} portfolio{t.portfolios_count === 1 ? "" : "s"} · {t.holdings_count} holding
              {t.holdings_count === 1 ? "" : "s"}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile label="Total invested" value={formatLKR(t.invested_amount, { compact: true, decimals: 2 })} icon={Wallet} testId="comb-kpi-invested" />
        <KpiTile label="Total current value" value={formatLKR(t.current_value, { compact: true, decimals: 2 })} icon={TrendingUp} testId="comb-kpi-current" />
        <KpiTile
          label="Total unrealised P/L"
          value={`${totalPositive ? "+" : ""}${formatLKR(t.gain_loss_lkr, { compact: true, decimals: 2 })}`}
          delta={t.gain_loss_pct}
          icon={totalPositive ? ArrowUpRight : ArrowDownRight}
          accent
          testId="comb-kpi-pl"
        />
        <KpiTile label="Total dividends received" value={formatLKR(t.dividend_income_lkr, { compact: true, decimals: 2 })} icon={Coins} testId="comb-kpi-divs" />
      </div>

      <Card className="border bg-card overflow-hidden">
        <CardHeader>
          <CardTitle className="font-display">Per-portfolio breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="combined-breakdown-table">
              <thead className="bg-card sticky top-0">
                <tr className="border-b">
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Portfolio</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Goal / Risk</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Holdings</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Invested</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Current value</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Return</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">Dividends</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      No portfolios yet.
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const positive = (r.gain_loss_lkr || 0) >= 0;
                  return (
                    <tr
                      key={r.portfolio_id}
                      data-testid={`combined-row-${r.portfolio_id}`}
                      onClick={() => onSelectPortfolio(r.portfolio_id)}
                      className="border-b last:border-0 hover:bg-muted/40 cursor-pointer transition-colors duration-150"
                    >
                      <td className="px-4 py-3 font-medium">{r.portfolio_name}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {r.goal && <Badge variant="secondary">{r.goal}</Badge>}
                          {r.risk_level && <Badge variant="outline" className="text-xs">{r.risk_level}</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular">{r.holdings_count}</td>
                      <td className="px-4 py-3 text-right tabular">{formatLKR(r.invested_amount)}</td>
                      <td className="px-4 py-3 text-right tabular">{formatLKR(r.current_value)}</td>
                      <td className={`px-4 py-3 text-right tabular ${positive ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
                        {positive ? "+" : ""}{formatLKR(r.gain_loss_lkr)}
                        <span className="ml-1 text-xs">({formatPct(r.gain_loss_pct)})</span>
                      </td>
                      <td className="px-4 py-3 text-right tabular">{formatLKR(r.dividend_income_lkr)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" className="gap-1" onClick={(e) => { e.stopPropagation(); onSelectPortfolio(r.portfolio_id); }}>
                          Open <ChevronRight className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortfolioCombinedView;
